import { FC, MutableRefObject, Key, useEffect, useMemo, useRef, useState } from "react";
// 组件
import { InfoCircleFilled } from "@ant-design/icons";
import { ProAlert, ProModal, ProTag } from "../../../antd";
import SuperTable from "../../../SuperTable";
// 常量
import { RowSelectionType, TableRowSelection } from "antd/lib/table/interface";
import { IselectedInfo, TableActionType } from "../../../SuperTable/interface";
import { ModalTableActionType } from "../../../constants/interface";
import { MoreModalBaseProps, MORE_MODAL_SIZE_ENUM } from "../../interface";
import styles from './style.module.less';

interface MoreModalTableProps extends MoreModalBaseProps {
  /** 行标识 */
  rowKey: string;
  /** label对应的字段 */
  labelKey?: string;
  /** 选择框类型 */
  rowSelectionType?: RowSelectionType;
  /** 已选数据 */
  selectedInfo?: IselectedInfo;
  /** 对外方法 */
  actionRef: MutableRefObject<ModalTableActionType>;
  /** 确定 */
  onOk: (selectedRowKeys?: Key[], selectedRows?: any[]) => void;
}

const MoreModalTable: FC<MoreModalTableProps> = props => {
  const {
    actionRef, rowKey, columns = [], rowSelectionType, moreTableShowTab, labelKey, formItems,
    selectedInfo, associateSelectNames, groupFields, moreModalSize, otherParams,
    fetchApi, onOk, getPopupContainer, beforeFetch,
  } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const [selectedData, setSelectedData] = useState<IselectedInfo>({
    ...selectedInfo,
  });
  const tableRef = useRef<TableActionType>();
  useEffect(() => {
    setSelectedData({ ...selectedData, ...selectedInfo });
  }, [selectedInfo]);
  /** 显示弹窗 */
  const handleShow = () => {
    setVisible(true);
  };
  /** 关闭弹窗 */
  const handleHide = () => {
    setVisible(false);
  };
  /** 清空 */
  const handleEmpty = () => {
    setSelectedData({
      selectedRowKeys: [],
      selectedRows: [],
    });
    tableRef.current?.emptySelect();
  };
  // tag关闭
  const handleClose = (key: string) => {
    const { selectedRowKeys, selectedRows } = selectedData;
    setSelectedData({
      selectedRowKeys: selectedRowKeys?.filter(v => v !== key),
      selectedRows: selectedRows?.filter(
        item => item[rowKey as string] !== key,
      ),
    });
  };
  const handleOk = () => {
    const { selectedRowKeys = [], selectedRows = [] } = selectedData;
    onOk && onOk(selectedRowKeys, selectedRows);
    handleHide();
  };
  const rowSelection: TableRowSelection<any> = useMemo(() => {
    return {
      type: rowSelectionType,
      selectedRowKeys: selectedData?.selectedRowKeys,
      onSelect: (record: any, selected: boolean) => {
        const { selectedRowKeys = [], selectedRows = [] } = selectedData;
        const keyValue: string = record[rowKey];
        if (selected) {
          if (rowSelectionType === 'checkbox') {
            setSelectedData({
              selectedRowKeys: [...selectedRowKeys, keyValue],
              selectedRows: [...selectedRows, record],
            });
          }
          if (rowSelectionType === 'radio') {
            setSelectedData({
              selectedRowKeys: [keyValue],
              selectedRows: [record],
            });
          }
        } else {
          setSelectedData({
            selectedRowKeys: selectedRowKeys.filter(v => v !== keyValue),
            selectedRows: selectedRows.filter(item => {
              const currentkeyValue: string = item[rowKey];
              return currentkeyValue !== keyValue;
            }),
          });
        }
      },
      onSelectAll: (selected, rows, changeRows) => {
        const { selectedRowKeys = [], selectedRows = [] } = selectedData;
        const changeRowsKeys: Key[] = changeRows.map(item => {
          const currentKey: string = item[rowKey];
          return currentKey;
        });
        if (selected) {
          setSelectedData({
            selectedRowKeys: [...selectedRowKeys, ...changeRowsKeys],
            selectedRows: [...selectedRows, ...changeRows],
          });
        } else {
          setSelectedData({
            selectedRowKeys: selectedRowKeys.filter(
              v => !changeRowsKeys.includes(v),
            ),
            selectedRows: selectedRows.filter(item => {
              const currentkeyValue: string = item[rowKey];
              return !changeRowsKeys.includes(currentkeyValue);
            }),
          });
        }
      },
    }
  }, [selectedData]);
  const modalWidth = useMemo(() => {
    if (!moreModalSize) return MORE_MODAL_SIZE_ENUM.middle;
    if (typeof moreModalSize === 'number') return moreModalSize;
    return MORE_MODAL_SIZE_ENUM[moreModalSize];
  }, [moreModalSize]);
  actionRef.current = {
    hide: handleHide,
    show: handleShow,
  };
  return (
    <ProModal
      title='选择更多'
      open={visible}
      destroyOnClose={true}
      maskClosable={false}
      onCancel={handleHide}
      onOk={handleOk}
      width={modalWidth}
      getContainer={getPopupContainer}
    >
      <SuperTable
        actionRef={tableRef}
        columns={columns}
        listApi={fetchApi}
        onBefore={beforeFetch}
        rowKey={rowKey}
        rowSelection={rowSelection}
        rowSelectionType={rowSelectionType}
        headOperation={false}
        toolBar={false}
        sceneType="modal"
        highSearch={true}
        formItems={formItems}
        operation={false}
        defauleSearchParams={otherParams}
        associateSelectNames={associateSelectNames}
        groupFields={groupFields}
        headerTip={
          <>
            {rowSelectionType === 'checkbox' ? (
              <>
                <ProAlert
                  className={styles.alert}
                  message={
                    <>
                      <div className={styles.wrapper}>
                        <div>
                          <InfoCircleFilled className={styles.icon} />
                          {!!selectedData.selectedRowKeys?.length ? (
                            <>
                              <span>已选择</span>
                              <span className={styles.num}>
                                {selectedData.selectedRowKeys?.length}
                              </span>
                              <span>项</span>
                            </>
                          ) : (
                            <span className={styles.select}>请选择</span>
                          )}
                        </div>
                        <span
                          className={`${styles.empty} modal-empty-btn`}
                          onClick={handleEmpty}
                        >
                          清空
                        </span>
                      </div>
                    </>
                  }
                  type="info"
                />
                {moreTableShowTab && !!labelKey && (
                  <div className={styles.tagWrapper}>
                    <span>已选项：</span>
                    {!selectedData.selectedRows?.length && <span>无</span>}
                    {selectedData.selectedRows?.map(item => {
                      const itemKey: string = item[rowKey];
                      return (
                        <ProTag
                          key={itemKey}
                          closable
                          onClose={e => {
                            e.preventDefault();
                            handleClose(itemKey);
                          }}
                        >
                          {item[labelKey]}
                        </ProTag>
                      );
                    })}
                  </div>
                )}
              </>
            ) : null}
          </>
        }
      />
    </ProModal>
  );
};

export default MoreModalTable;
